import type { FavoriteData } from "owa-favorites-types";
import favoritesStore from "../../store/store";
import { getModuleContextMailboxInfo } from "owa-module-context-mailboxinfo";
import { getAccountKeyForMailboxInfo } from "owa-client-types";
import { isMonarchMultipleAccountsEnabled } from "owa-account-source-list";
import { addFavoriteToCache, removeFavoriteFromCache } from "./offlineCacheHelpers";
import setFavoriteDataToStore from "./setFavoriteDataInStore";
import { getUniqueDataForFavoriteType } from "../../utils/getUniqueDataForFavoriteType";

export default function addFavoriteDataToStore(favoriteData: FavoriteData, newIndex?: number, isVisible?: boolean): void {
    const accountKey = getAccountKeyForMailboxInfo(favoriteData?.mailboxInfo ?? getModuleContextMailboxInfo());
    const orderedOutlookFavoritesIds = isMonarchMultipleAccountsEnabled()
        ? favoritesStore().orderedCombinedOutlookFavoritesIds
        : favoritesStore().favoriteTreeData.get(accountKey)?.orderedOutlookFavoritesIds;
    
    // Check whether this favoriteId already exists in the list.
    // If so, do nothing as Prime returned an already existing favorite (Upsert).
    if (!orderedOutlookFavoritesIds ||
        orderedOutlookFavoritesIds.some(id => id === favoriteData.favoriteId)) {
        return;
    }
    
    // In Monarch, check if an entry with the same uniqueId but a different favoriteId
    // already exists (e.g. an offline placeholder ID being replaced by the real server ID).
    // If so, replace the old entry to prevent duplicates.
    if (isMonarchMultipleAccountsEnabled()) {
        const newUniqueId = getUniqueDataForFavoriteType(favoriteData);
        if (newUniqueId) {
            const { outlookFavorites } = favoritesStore();
            for (const [existingId, existingData] of outlookFavorites) {
                if (existingId !== favoriteData.favoriteId &&
                    existingData.type === favoriteData.type &&
                    getUniqueDataForFavoriteType(existingData).toLocaleLowerCase() ===
                        newUniqueId.toLocaleLowerCase()) {
                    // Remove the old entry from the ordered list
                    const oldIndex = orderedOutlookFavoritesIds.indexOf(existingId);
                    if (oldIndex >= 0) {
                        orderedOutlookFavoritesIds.splice(oldIndex, 1);
                        // Preserve the old entry's position if no explicit newIndex
                        if (newIndex === undefined) {
                            newIndex = oldIndex;
                        }
                    }
                    outlookFavorites.delete(existingId);
                    removeFavoriteFromCache(existingData);
                    break;
                }
            }
        }
    }
    
    // Local update to add node to favorites store
    if (newIndex !== undefined) {
        orderedOutlookFavoritesIds.splice(newIndex, 0, favoriteData.favoriteId);
    }
    else {
        orderedOutlookFavoritesIds.push(favoriteData.favoriteId);
    }
    
    if (isMonarchMultipleAccountsEnabled()) {
        addFavoriteToCache(favoriteData, newIndex, isVisible);
    }
    
    setFavoriteDataToStore(favoriteData);
}
